import type { BotApi } from './BotApi';
import type { BotCommand, BotSlot, CommandRequest, IntelligenceRequest, SaveSlotRequest } from '../domain/types';
import { mockAdminOverview, mockCommands, mockSlots, mockStatus, mockUser } from './mockData';

let slots: BotSlot[] = [...mockSlots];
let commands: BotCommand[] = [...mockCommands];
let intelligenceRequests: IntelligenceRequest[] = [];

export const mockBotApi: BotApi = {
  mode: 'mock',
  allowCommands: true,
  allowedCommandSlotKeys: [],
  async getCurrentUser() {
    return mockUser;
  },
  async listSlots() {
    return slots;
  },
  async getSlotStatus(slotId) {
    return slotId === mockStatus.slotId ? mockStatus : null;
  },
  async listCommands(slotId) {
    return commands.filter((command) => command.commandId.includes(slotId)).slice(0, 10);
  },
  async sendCommand(request: CommandRequest) {
    const command: BotCommand = {
      commandId: `mock_${request.slotId}_${Date.now()}`,
      action: request.action,
      status: 'queued',
      queuedAt: new Date().toISOString()
    };
    commands = [command, ...commands];
    return command;
  },
  async listIntelligenceRequests() {
    return intelligenceRequests;
  },
  async submitIntelligenceRequest(playerName: string, regionId?: string) {
    const id = `mock_intel_${Date.now()}`;
    intelligenceRequests = [
      {
        id,
        playerName,
        regionId: regionId?.trim() || null,
        status: 'pending',
        createdAt: new Date().toISOString(),
        results: []
      },
      ...intelligenceRequests
    ];
    return id;
  },
  async deleteIntelligenceRequest(requestId: string) {
    intelligenceRequests = intelligenceRequests.map((request) =>
      request.id === requestId ? { ...request, hiddenAt: new Date().toISOString() } : request
    );
  },
  async getAdminOverview() {
    return { ...mockAdminOverview, slots };
  },
  async getAuditEvents(options = {}) {
    const limit = Math.max(1, Math.min(500, options.limit ?? 300));
    const offset = Math.max(0, options.offset ?? 0);
    const events = options.slotId
      ? (mockAdminOverview.auditEvents || []).filter((event) => event.slotId === options.slotId)
      : options.agentId
        ? (mockAdminOverview.auditEvents || []).filter((event) => event.agentId === options.agentId)
      : (mockAdminOverview.auditEvents || []);
    const pageEvents = events.slice(offset, offset + limit);
    return {
      events: pageEvents,
      limit,
      offset,
      total: events.length,
      hasMore: offset + pageEvents.length < events.length
    };
  },
  async saveSlot(request: SaveSlotRequest) {
    const existing = slots.find((slot) => slot.slotKey === request.slotKey);
    const slot: BotSlot = {
      id: existing?.id || request.slotKey,
      slotKey: request.slotKey,
      label: request.label,
      userEmail: mockUser.email,
      agentId: request.agentId,
      licenseStatus: request.licenseStatus,
      licensePlan: request.licensePlan,
      planKey: request.licensePlan === 'pro_plus' ? 'pro_plus' : request.licensePlan,
      linked: request.linked,
      state: request.linked ? 'linked' : 'empty',
      enabledModules: request.enabledModules,
      regionId: request.regionId?.trim() || existing?.regionId || null,
      regionStatus: request.regionId?.trim() ? 'verified' : existing?.regionStatus,
      regionSource: request.regionId?.trim() ? 'admin' : existing?.regionSource,
      regionVerifiedAt: request.regionId?.trim() ? new Date().toISOString() : existing?.regionVerifiedAt
    };
    slots = [slot, ...slots.filter((item) => item.slotKey !== request.slotKey)];
    return slot;
  },
  async updateSlotDesiredState(slotId, update) {
    const slot = slots.find((item) => item.id === slotId);
    if (!slot) throw new Error('slot not found');
    const updated = {
      ...slot,
      desiredWorkerState: update.desiredWorkerState,
      desiredBotState: update.desiredBotState,
      desiredReason: update.desiredReason,
      desiredUpdatedAt: new Date().toISOString()
    };
    slots = slots.map((item) => (item.id === slotId ? updated : item));
    return updated;
  },
  async releaseSlot(slotId: string) {
    slots = slots.filter((slot) => slot.id !== slotId);
  },
  async sendAdminSlotCommand(_action, _payload) {
    // mock: no-op
    return { commandId: `mock_${Date.now()}` };
  }
};
